import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PanelLeftClose, PanelLeft } from 'lucide-react';
import { cn } from '../../lib/utils';
import { TooltipProvider } from '../ui/tooltip';
import { NAV_GROUPS, filterVisibleGroups, isItemActive } from './sidebarConfig';
import { SidebarNavItem } from './SidebarNavItem';
import { SidebarUserCard } from './SidebarUserCard';

/* Lockup mark — monoline lightbulb + filament + amber dot (matches login lockup). */
function LumynMark({ collapsed }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="relative inline-flex items-center justify-center w-8 h-8">
        <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-gold">
          <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.5.4.8 1 .8 1.6V16h5.4v-.5c0-.6.3-1.2.8-1.6A6 6 0 0 0 12 3Z"
                stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="12" cy="8.5" r="1.1" fill="currentColor"/>
        </svg>
      </span>
      {!collapsed && (
        <div className="flex flex-col leading-none">
          <span className="text-lg font-display font-semibold tracking-wide">Lumyn</span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 mt-0.5">trainer ops</span>
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ collapsed, onToggle }) {
  const { user, logout, hasRole, hasPermission } = useAuth();

  const location = useLocation();

  const visibleGroups = filterVisibleGroups(NAV_GROUPS, hasRole, hasPermission);

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        data-testid="sidebar"
        className={cn(
          'fixed left-0 top-0 bottom-0 z-30 flex flex-col border-r bg-card transition-[width] duration-200',
          collapsed ? 'w-[var(--sidebar-collapsed)]' : 'w-[var(--sidebar-width)]',
        )}
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b shrink-0">
          <LumynMark collapsed={collapsed} />
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-2.5 space-y-5">
          {visibleGroups.map((group, gi) => (
            <div key={group.label} className="space-y-1">
              {!collapsed && (
                <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/60">
                  {group.label}
                </div>
              )}
              {collapsed && gi > 0 && <div className="my-2 border-t mx-2" />}
              {group.items.map(item => (
                <SidebarNavItem
                  key={item.to}
                  item={item}
                  active={isItemActive(item, location.pathname)}
                  collapsed={collapsed}
                />
              ))}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t p-3 space-y-1 shrink-0">
          <SidebarUserCard user={user} collapsed={collapsed} onLogout={logout} />

          <button
            onClick={onToggle}
            className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted/60 transition-colors"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>
    </TooltipProvider>
  );
}
